import Vue from 'vue'
import axios from 'axios'

import {CMS_RANKS_LIST} from './ConstantService'

// Whether CMS can be reached, for the banner at the top of every page (CmsStatusBanner).
// Every KIDS API call for CMS data (the API's CmsController routes below) updates it: a CMS
// that is down (user, 2026-10-22) answers with an HTTP error, nothing at all, or a 200 that
// only says {"message": "Server Error"} or {} (CMS_DECODED_ENDPOINTS). Those calls then fail
// without an error popup of their own - the banner says it once - and the next call that gets
// an answer hides it.
// The banner belongs to the page whose call failed: opening another page hides it (that
// page shows it again if its own CMS call fails), its tabs keep it.
export const CMS_UNAVAILABLE_MESSAGE = 'CMS is temporarily unavailable, so crew information may not appear. You can still work on and save your reports. Crew information will show again once CMS is back.'
export const CMS_CHECK_TIMEOUT_MS = 30000

const CMS_ENDPOINTS = [
  'getManningCrewStats', 'getManningCrew', 'getOnboardCrew', 'getCrewAgeAndRankStats', 'getCrewBasedOnAgeAndRank',
  'getAllVesselsWithOnBoardCrews', 'getAllVesselsWithCurrentOnBoardCrews', 'getOnboardCrewNationalityStats',
  'getCmsCrewNationality', 'getCrewEmbarkDisembarkHistory', 'getRanks', 'getCountries', 'getPorts',
  'getAllVesselManningCompany', 'getCrewInfo', 'getCrewActivities', 'getCrewImage', 'getCrewConsent', 'getCrewBiodata',
  'getCrewExperience', 'getCrewExternalExperience', 'getCrewIncidentBehavior', 'getCrewIncidentBehaviorFile',
  'getCrewDocuments', 'getCrewDocumentHistory', 'getCrewDocumentFile', 'getCrewDocumentsByFlag', 'getAssessments',
  'getAssessmentFile', 'getAssessmentHistory', 'getMedicalKeys', 'getCrewMedicals', 'getHealthDeclarations',
  'getCrewRtPcr', 'getCovidVaccines', 'getCrewMedicalFile', 'getCovidVaccineFile', 'getRtpcrTestFile',
  'getHealthDeclarationFile', 'getCrewEvaluationReports', 'getCrewEvaluationFile', 'getCrewCommentAttachment',
  'getCompanyEvaluations', 'getCompanyEvaluationFile', 'getMedicalIssues', 'getPandIs', 'getPandICheckupAttachment',
  'getPandiExpenseAttachment', 'getMedicalIncidentFile', 'getMedicalInjuryFile', 'getMedicalHospitalizationFile',
]

// The lookups the KIDS API decodes before answering: once it holds a CMS token, a CMS failure
// comes back from these as 200 {} instead of an error. Their real answers are lists or filled
// objects.
const CMS_DECODED_ENDPOINTS = [
  'getRanks', 'getCountries', 'getPorts', 'getManningCrew', 'getCrewAgeAndRankStats', 'getCrewBasedOnAgeAndRank',
  'getAllVesselsWithOnBoardCrews', 'getAllVesselsWithCurrentOnBoardCrews', 'getOnboardCrewNationalityStats',
  'getCrewEmbarkDisembarkHistory',
]

export const CmsStatus = Vue.observable({
  unavailable: false,
  checking: false,
  stillUnavailable: false,
})

// The page the banner is shown for (see pageOf).
let unavailablePage = null

export const CmsStatusService = {
  endpointOf(config) {
    const path = String((config && config.url) || '').split('?')[0]
    const match = path.match(/(?:^|\/)kids\/([A-Za-z]+)$/)
    return match ? match[1] : null
  },

  isCmsRequest(config) {
    return CMS_ENDPOINTS.includes(this.endpointOf(config))
  },

  // The KIDS API's own answer when it couldn't get a CMS token.
  isServerErrorBody(data) {
    return !!data && typeof data === 'object' && !Array.isArray(data)
      && Object.keys(data).length === 1 && data.message === 'Server Error'
  },

  // No answer (CMS hung past a timeout, connection dropped) or a server error. Not a 503:
  // that is KIDS' own maintenance mode.
  isUnavailableError(error) {
    if (axios.isCancel(error)) return false
    const status = error && error.response ? error.response.status : null
    return status === null || (status >= 500 && status !== 503)
  },

  // A 200 that is really CMS failing: isServerErrorBody, or {} from a CMS_DECODED_ENDPOINTS lookup.
  isUnavailableAnswer(response) {
    const data = response.data
    if (this.isServerErrorBody(data)) return true
    return CMS_DECODED_ENDPOINTS.includes(this.endpointOf(response.config))
      && !!data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0
  },

  // A 200 that isUnavailableAnswer, turned into the same failure as a 500 so each caller
  // takes its own error path.
  unavailableError(response) {
    const error = new Error('CMS is unavailable')
    error.config = response.config
    error.response = {...response, status: 500}
    error.cmsUnavailable = true
    return error
  },

  // The page a request belongs to: the component of the first route level with a parameter
  // (the report, crew, vessel or dashboard page, which its tabs share), else of the last level.
  // Its route path while that component doesn't exist yet.
  pageOf(route) {
    const matched = (route && route.matched) || []
    const record = matched.find(r => r.path.includes(':')) || matched[matched.length - 1]
    return (record && record.instances.default) || (route ? route.path : null)
  },

  isOnPage(page, route) {
    return typeof page === 'string' ? page === route.path : page === this.pageOf(route)
  },

  markAvailable() {
    unavailablePage = null
    CmsStatus.unavailable = false
    CmsStatus.stillUnavailable = false
  },

  // A request made on a page the user has since left doesn't bring the banner back.
  markUnavailable(requestPage, currentRoute) {
    if (!this.isOnPage(requestPage, currentRoute)) return
    unavailablePage = requestPage
    CmsStatus.unavailable = true
  },

  pageChanged(route) {
    if (CmsStatus.unavailable && !this.isOnPage(unavailablePage, route)) {
      this.markAvailable()
    }
  },

  // "Try again": asks for the CMS rank list. Resolves true when CMS answered.
  async check() {
    CmsStatus.checking = true
    try {
      await axios.get(CMS_RANKS_LIST, {timeout: CMS_CHECK_TIMEOUT_MS})
      return true
    } catch (error) {
      CmsStatus.stillUnavailable = true
      return false
    } finally {
      CmsStatus.checking = false
    }
  },
}
